import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltip } from '@angular/material/tooltip';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { TranslateModule } from '@ngx-translate/core';
import {
  debounceTime,
  distinctUntilChanged,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import {
  BookingFormData,
  BookingSchedulingPolicyFormData,
  BookingServiceFormData,
} from '../../_models/bookingFormData';
import { BookingReqDto } from '../../_dtos/bookingReqDto';
import { DepartmentResDto } from '../../_dtos/departmentResDto';
import { UserResDto } from '../../_dtos/userResDto';
import { AuthService } from '../../_services/auth.service';
import { BookingsService } from '../../_services/bookings.service';
import { DepartmentsService } from '../../_services/departments.service';
import { ErrorMessageService } from '../../_services/error-message.service';
import { GraphService } from '../../_services/graph.service';
import { SharedService } from '../../_services/shared.service';
import { TranslatedToastService } from '../../_services/translated-toast.service';
import { ValidatorService } from '../../_services/validator.service';

@Component({
  selector: 'app-order-form-bookings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatSelect,
    MatOption,
    MatButton,
    MatCheckbox,
    MatExpansionModule,
    MatTooltip,
    MatAutocomplete,
    MatAutocompleteTrigger,
    TranslateModule,
  ],
  templateUrl: './order-form-bookings.component.html',
  styleUrls: ['./order-form-bookings.component.css'],
})
export class OrderFormBookingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  public readonly maxServices = 4;
  public bookingForm!: FormGroup<BookingFormData>;
  public departments!: DepartmentResDto[];
  public filteredPeople?: Observable<UserResDto[]>;

  constructor(
    private authService: AuthService,
    private bookingsService: BookingsService,
    private departmentsService: DepartmentsService,
    private errorMessageService: ErrorMessageService,
    private graphService: GraphService,
    private sharedService: SharedService,
    private toastr: TranslatedToastService,
    private validatorService: ValidatorService,
  ) {}

  ngOnInit() {
    this.departments = this.departmentsService.getDepartments();
    this.initializeForm();
    this.setupFormListeners();
    this.prefillForm();
    this.setupSecondaryOwnerAutocomplete();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get name() {
    return this.bookingForm.controls.name;
  }

  get secondaryOwner() {
    return this.bookingForm.controls.secondaryOwner;
  }

  get services() {
    return this.bookingForm.controls.services;
  }

  get schedulingPolicy() {
    return this.bookingForm.controls.schedulingPolicy;
  }

  get nameErrorMessage() {
    if (this.name.hasError('required')) return 'Name is required';
    if (this.name.hasError('hasForbiddenChars'))
      return 'Contains forbidden Character';
    if (this.name.hasError('exceedsMaxLength'))
      return 'The provided name is too long';
    if (this.name.hasError('noAddressCharacters'))
      return 'BOOKINGS_NAME_NO_ADDRESS_CHARACTERS';
    if (this.name.hasError('addressTooLong'))
      return 'BOOKINGS_NAME_ADDRESS_TOO_LONG';
    if (this.name.hasError('nameInUse')) return 'This name is already taken';
    if (this.name.hasError('nameCheckFailed'))
      return 'BOOKINGS_NAME_CHECK_FAILED';
    return '';
  }

  get secondaryOwnerErrorMessage() {
    if (this.secondaryOwner.hasError('isCurrentUser'))
      return 'BOOKINGS_OWNER_IS_CURRENT_USER';
    if (this.secondaryOwner.hasError('notBfhUser'))
      return 'BOOKINGS_OWNER_NOT_BFH_USER';
    if (this.secondaryOwner.hasError('noMailbox'))
      return 'BOOKINGS_OWNER_NO_MAILBOX';
    if (this.secondaryOwner.hasError('mailboxCheckFailed'))
      return 'BOOKINGS_OWNER_CHECK_FAILED';
    return 'Please select a valid user from the dropdown list';
  }

  displayUserFn(user: UserResDto | null): string {
    return user ? `${user.displayName}` : '';
  }

  onSecondaryOwnerSelected(event: MatAutocompleteSelectedEvent) {
    this.secondaryOwner.setValue(event.option.value as UserResDto);
  }

  addService() {
    if (this.services.length >= this.maxServices) return;

    this.services.push(
      new FormGroup<BookingServiceFormData>({
        name: new FormControl('', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        durationMinutes: new FormControl(30, {
          nonNullable: true,
          validators: this.rangeValidators(5, 1440),
        }),
      }),
    );
  }

  removeService(index: number) {
    this.services.removeAt(index);
  }

  async onSubmit() {
    if (!this.bookingForm.valid) return;

    const formValues = this.bookingForm.getRawValue();
    const name = formValues.name.trim();
    const city = formValues.city.trim();
    const postalCode = formValues.postalCode.trim();
    // A valid form only holds a user selected from the autocomplete
    const secondaryOwner =
      typeof formValues.secondaryOwner === 'object'
        ? formValues.secondaryOwner?.userPrincipalName
        : undefined;

    const bookingReqDto: BookingReqDto = {
      name: name,
      secondaryOwner: secondaryOwner,
      department: formValues.department,
      address:
        city || postalCode
          ? { city: city || undefined, postalCode: postalCode || undefined }
          : undefined,
      schedulingPolicy: formValues.schedulingPolicy,
      services: formValues.services.map((service) => ({
        name: service.name.trim(),
        durationMinutes: service.durationMinutes,
      })),
      publish: formValues.publish,
    };

    this.sharedService.closeModal();
    await this.toastr.show(
      'success',
      'TOASTS.BOOKINGS.SUBMIT_MESSAGE',
      'TOASTS.BOOKINGS.SUBMIT_TITLE',
    );

    this.bookingsService.createBooking(bookingReqDto).subscribe({
      next: async (booking) => {
        await this.toastr.show(
          'success',
          'TOASTS.BOOKINGS.CREATION_SUCCESS_MESSAGE',
          'TOASTS.BOOKINGS.CREATION_SUCCESS_TITLE',
          { displayName: booking.displayName, email: booking.email },
        );
      },
      // SkipErrorToast is set on the request: this toast names the calendar instead of the generic one
      error: async (error) => {
        await this.errorMessageService.showToast(
          error,
          'TOASTS.BOOKINGS.CREATION_FAILED_TITLE',
          { displayName: name },
        );
      },
    });
  }

  private initializeForm() {
    this.bookingForm = new FormGroup<BookingFormData>({
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
        asyncValidators: [this.validatorService.validateBookingsName()],
      }),
      address: new FormControl(
        { value: '', disabled: true },
        { nonNullable: true },
      ),
      primaryOwner: new FormControl(
        { value: this.authService.getDisplayName(), disabled: true },
        { nonNullable: true },
      ),
      secondaryOwner: new FormControl<UserResDto | string | null>(null, {
        validators: [
          this.validatorService.userObjectValidator(),
          this.validatorService.notUserValidator(
            this.authService.getUsername(),
          ),
          this.validatorService.bfhUpnValidator(),
        ],
        asyncValidators: [this.validatorService.mailboxValidator()],
      }),
      department: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      city: new FormControl('', { nonNullable: true }),
      postalCode: new FormControl('', { nonNullable: true }),
      services: new FormArray<FormGroup<BookingServiceFormData>>([]),
      // Defaults of the business; limits as the [Range] attributes of the Core API
      schedulingPolicy: new FormGroup<BookingSchedulingPolicyFormData>({
        timeSlotIntervalMinutes: new FormControl(30, {
          nonNullable: true,
          validators: this.rangeValidators(5, 1440),
        }),
        minimumLeadTimeHours: new FormControl(24, {
          nonNullable: true,
          validators: this.rangeValidators(0, 8760),
        }),
        maximumAdvanceDays: new FormControl(60, {
          nonNullable: true,
          validators: this.rangeValidators(1, 365),
        }),
        allowStaffSelection: new FormControl(false, { nonNullable: true }),
        sendConfirmationsToOwner: new FormControl(true, { nonNullable: true }),
      }),
      publish: new FormControl(false, { nonNullable: true }),
    });
  }

  private rangeValidators(min: number, max: number) {
    return [
      Validators.required,
      Validators.min(min),
      Validators.max(max),
      this.validatorService.integerValidator(),
    ];
  }

  private prefillForm() {
    this.bookingForm.patchValue({
      department: this.departmentsService.getDepartment().shortName,
    });

    // City and postal code come from /me and may arrive after the form was opened
    this.graphService.city$
      .pipe(takeUntil(this.destroy$))
      .subscribe((city) => {
        const control = this.bookingForm.controls.city;
        if (city && control.pristine) control.setValue(city);
      });
    this.graphService.postalCode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((postalCode) => {
        const control = this.bookingForm.controls.postalCode;
        if (postalCode && control.pristine) control.setValue(postalCode);
      });
  }

  private setupFormListeners() {
    this.name.valueChanges
      .pipe(startWith(this.name.value), takeUntil(this.destroy$))
      .subscribe((value) => {
        const hasName = !!value.trim();
        this.bookingForm.controls.address.setValue(
          hasName ? this.validatorService.bookingsAddress(value) : '',
          { emitEvent: false },
        );
      });
  }

  private setupSecondaryOwnerAutocomplete() {
    this.filteredPeople = this.secondaryOwner.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchValue) => {
        if (typeof searchValue !== 'string' || !searchValue.trim())
          return of([]);

        return this.graphService.searchPeople(searchValue.trim());
      }),
    );
  }
}
